import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Grid, IconButton, Typography } from '@mui/material';
import {
	CalenderIcon,
	CareNoteIcon,
	ICPIcon,
	OCPIcon,
	PlanPerIcon,
} from '../../assets/icons/Icons';
import SideNavigationBar from '../../components/SideNavigationBar';
import { useMainCustomStyle } from '../../assets/theme/Theme';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
	ArcElement,
	BarElement,
	CategoryScale,
	Chart,
	Legend,
	LinearScale,
	LineElement,
	PointElement,
	Title,
} from 'chart.js';
import { MAIN_THEME_COLOR } from '../../assets/theme/Colors';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import { DateRange } from 'react-date-range';
import { enUS } from 'date-fns/locale';
import ResidentTable from './ResidentTable';
import {
	DataTile,
	FilterTile,
	ItemTile,
	MetricTile,
	UserAnalyticsSection,
} from './DataTiles';
import {
	deleteResident,
	fetchAllCarePlanData,
	fetchCarePlanById,
	fetchCarePlanUpdateOverTIme,
	fetchDashboardData,
	fetchElder,
	fetchElderCareNotes,
	fetchElderDataFile,
	fetchElderFiles,
	fetchExternalElder,
	filterCarePlanStat,
	updateElderDetails,
} from '../../services/ElderService';
import { getAccessToken, getElderHomeId } from '../../Utils/AuthUtil';
import ResidentOverview from './ResidentOverview';
import { DATE_FILTER, DAYS, ERROR_STATE, MONTHS } from '../../Utils/Enums';
import DocumentOverview from './DocumentOverview';
import PlanOverview from './PlanOverview';
import { toast } from 'react-toastify';
import { dateConverter } from '../../Utils/Helpers';

Chart.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Legend,
	ArcElement,
	BarElement,
);

const GRAPH_COLORS = {
	cn: '#016A6D',
	icp: '#1D9190',
	ocp: '#01C6B2',
};

const ITEMS_NAME = {
	cn: 'Care Notes',
	icp: 'Initial Plans',
	ocp: 'Optimized Plans',
};

const ITEM_FILTER = [
	'All',
	'Care Note',
	'Initial Care Plan',
	'Optimized Care Plan',
];

const FILE_ITEM_FILTER = [
	'All',
	'Onboarding',
	'Initial Care Plan',
	'Optimized Care Plan',
];

const VIEW_TYPES = {
	table: 'TableOverView',
	document: 'DocumentOverView',
	plan: 'PlanOverView',
	resident: 'ResidentOverView',
};

const INITIAL_DATA_SET = {
	label: '',
	data: [],
	borderColor: '',
	backgroundColor: '',
};

const RESIDENT_FORM_ERROR_STATE = {
	name: ERROR_STATE,
	gender: ERROR_STATE,
	age: ERROR_STATE,
};

const CHART_INITIAL_STATE = {
	labels: [],
	datasets: [
		{
			...INITIAL_DATA_SET,
			backgroundColor: GRAPH_COLORS.cn,
			borderColor: GRAPH_COLORS.cn,
		},
		{
			...INITIAL_DATA_SET,
			backgroundColor: GRAPH_COLORS.icp,
			borderColor: GRAPH_COLORS.icp,
		},
		{
			...INITIAL_DATA_SET,
			backgroundColor: GRAPH_COLORS.ocp,
			borderColor: GRAPH_COLORS.ocp,
		},
	],
};

const DOUGH_NUT_CHART_INITIAL_STATE = {
	labels: [],
	datasets: [
		{
			data: [],
			backgroundColor: [
				GRAPH_COLORS.cn,
				GRAPH_COLORS.icp,
				GRAPH_COLORS.ocp,
			],
			borderWidth: 0,
			cutout: '80%',
		},
	],
};

const lineCharOptions = {
	responsive: true,
	plugins: {
		datalabels: {
			display: false,
		},
		legend: {
			display: false,
		},
	},
	scales: {
		y: {
			ticks: {
				precision: 0,
			},
		},
	},
};

const doughnutChartOptions = {
	responsive: true,
	maintainAspectRatio: false,
	plugins: {
		legend: {
			display: false,
		},
		datalabels: {
			display: true,
			formatter: value => {
				return `${value}%`;
			},
			font: {
				weight: 'bold',
			},
			color: '#FFFFFF',
		},
	},
};

const barCharOptions = {
	responsive: true,
	maintainAspectRatio: false,
	scales: {
		x: {
			stacked: true,
			ticks: {
				maxRotation: 70,
				minRotation: 70,
				callback: function (value) {
					const label = this.getLabelForValue(value);
					return label.length > 15
						? label.substring(0, 15) + '...'
						: label;
				},
			},
		},
		y: {
			stacked: true,
			ticks: {
				precision: 0,
			},
		},
	},
	plugins: {
		legend: {
			display: false,
		},
	},
};

function SystemUsageDashboard() {
	const pickerRef = useRef();
	const scrollRef = useRef(null);
	const token = getAccessToken();
	const homeId = getElderHomeId();
	const [viewType, setViewType] = useState(null);
	const [previousView, setPreviousView] = useState(null);
	const [metrics, setMetrics] = useState({
		cn: 0,
		icp: 0,
		ocp: 0,
		planPer: 0,
	});
	const [careStatFrequency, setCareStatFrequency] = useState({
		cn: 0,
		icp: 0,
		ocp: 0,
		total: 0,
	});
	const [residents, setResidents] = useState([]);
	const [residentsRE, setResidentsRE] = useState([]);
	const [residentActivityChart, setResidentActivityChart] =
		useState(CHART_INITIAL_STATE);
	const [careStatusChart, setCareStatusChart] = useState(
		DOUGH_NUT_CHART_INITIAL_STATE,
	);
	const [planUpdatesChart, setPlanUpdatesChart] =
		useState(CHART_INITIAL_STATE);
	const [allData, setAllData] = useState([]);
	const [allDataRE, setAllDataRE] = useState([]);
	const [rowsPerPage, setRowsPerPage] = useState(5);
	const [page, setPage] = useState(0);
	const [searchedText, setSearchedText] = useState('');
	const [lineChartFilter, setLineChartFilter] = useState(DATE_FILTER[0]);
	const [updateTabFilter, setUpdateTabFilter] = useState(ITEM_FILTER[0]);
	const [showPicker, setShowPicker] = useState(false);
	const [range, setRange] = useState([
		{
			startDate: new Date(),
			endDate: new Date(),
			key: 'selection',
		},
	]);
	const [filterButtonText, setFilterButtonText] = useState(
		new Date().toLocaleString('default', { month: 'long' }),
	);
	const [resident, setResident] = useState({});
	const [isResidentNotEditable, setResidentNotEditable] = useState(true);
	const [reErrors, setReErrors] = useState(RESIDENT_FORM_ERROR_STATE);
	const [isResidentSubmitting, setIsResidentSubmitting] = useState(false);
	const [selectedDocType, setSelectedDocType] = useState(ITEM_FILTER[0]);
	const [residentFiles, setResidentFiles] = useState([]);
	const [residentReFiles, setResidentReFiles] = useState([]);
	const [selectedPlanData, setSelectedPlanData] = useState([]);
	const [selectedPlanType, setSelectedPlanType] = useState('');
	const [selectedElder, setSelectedElder] = useState('');
	const [expandedAccordion, setExpandedAccordion] = useState(null);
	const [residentCareNotes, setResidentCareNotes] = useState([]);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		function handleClickOutside(event) {
			if (
				pickerRef.current &&
				!pickerRef.current.contains(event.target)
			) {
				setShowPicker(false);
			}
		}
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	useEffect(() => {
		onFetchAndStructureAllData();
	}, []);

	useEffect(() => {
		if (scrollRef.current) {
			scrollRef.current.scrollTop = 0;
		}
	}, [allData]);

	async function onFetchAndStructureAllData() {
		setIsLoading(true);
		const data = await fetchDashboardData(homeId, token);
		const planData = await fetchAllCarePlanData(homeId, token);

		const date = new Date();
		const startDate = new Date(
			date.getFullYear(),
			date.getMonth(),
			1,
		).toISOString();
		const endDate = new Date(
			date.getFullYear(),
			date.getMonth() + 1,
			0,
		).toISOString();

		const currentMonthData = await filterCarePlanStat(
			homeId,
			token,
			startDate,
			endDate,
		);

		const counts = currentMonthData.counts;
		const activityData = currentMonthData.activity;
		onSetGraphData(counts, activityData, data.residentTable.length);

		const uploadData = data.byDate;
		setPlanUpdatesChart(prevState => ({
			...prevState,
			labels: DAYS,
			datasets: [
				{
					...prevState.datasets[0],
					data: Object.values(uploadData.cn),
				},
				{
					...prevState.datasets[1],
					data: Object.values(uploadData.icp),
				},
				{
					...prevState.datasets[2],
					data: Object.values(uploadData.ocp),
				},
			],
		}));

		setResidents(data.residentTable);
		setResidentsRE(data.residentTable);
		setAllData(planData);
		setAllDataRE(planData);
		setIsLoading(false);
	}

	async function getCarePlanStats(fetchAllTimeData = false) {
		setShowPicker(false);
		const start = range[0].startDate.toISOString();
		const end = range[0].endDate.toISOString();

		let response = {};
		if (fetchAllTimeData) {
			const responseObj = await fetchDashboardData(homeId, token);
			response = {
				counts: responseObj.counts,
				activity: responseObj.activity,
			};
			getSortButtonText(range, true);
		} else {
			response = await filterCarePlanStat(homeId, token, start, end);
			getSortButtonText(range);
		}

		if (!response.error) {
			const counts = response.counts;
			const activities = response.activity;
			onSetGraphData(counts, activities, residentsRE.length);
		} else {
			toast.error(response.message);
		}
	}

	function onSetGraphData(counts, activities, residentsCount) {
		const cnSize = counts.cn;
		const ipSize = counts.icp;
		const opSize = counts.ocp;
		const totalData = cnSize + ipSize + opSize;
		let opFrequency = 0;
		if (totalData > 0) {
			opFrequency = (totalData / residentsCount) * 100;
		}

		setMetrics({
			cn: cnSize,
			icp: ipSize,
			ocp: opSize,
			planPer: opFrequency.toFixed(2),
		});

		const calcPercent = (size, total) =>
			total > 0 ? ((size / total) * 100).toFixed(0) : 0;

		const cnFreq = calcPercent(cnSize, totalData);
		const icpFreq = calcPercent(ipSize, totalData);
		const ocpFreq = calcPercent(opSize, totalData);

		const totalFreq =
			parseFloat(cnFreq) + parseFloat(icpFreq) + parseFloat(ocpFreq);

		let cn = parseFloat(cnFreq);
		let icp = parseFloat(icpFreq);
		let ocp = parseFloat(ocpFreq);

		if (totalData > 0 && totalFreq !== 100) {
			const diff = 100 - totalFreq;
			const maxVal = Math.max(cn, icp, ocp);
			if (cn === maxVal) cn += diff;
			else if (icp === maxVal) icp += diff;
			else ocp += diff;
		}

		setCareStatFrequency({ cn, icp, ocp, total: totalData > 0 ? 100 : 0 });

		setCareStatusChart(prevState => ({
			...prevState,
			datasets: [
				{
					...prevState.datasets[0],
					data: [
						(cnSize / totalData) * 100,
						(ipSize / totalData) * 100,
						(opSize / totalData) * 100,
					],
				},
			],
		}));

		const rLabels = activities.map(item => item.name);
		setResidentActivityChart(prevState => ({
			...prevState,
			labels: rLabels,
			datasets: [
				{
					...prevState.datasets[0],
					data: activities.map(item => item.cn),
				},
				{
					...prevState.datasets[1],
					data: activities.map(item => item.icp),
				},
				{
					...prevState.datasets[2],
					data: activities.map(item => item.ocp),
				},
			],
		}));
	}

	function onSearch(search) {
		setSearchedText(search);
		const searchText = search.toLowerCase();

		if (search === '') {
			setResidents(residentsRE);
		} else {
			const searchResults = residentsRE.filter(value => {
				return (
					value.elderId.toLowerCase().includes(searchText) ||
					value.name.toLowerCase().includes(searchText)
				);
			});

			if (searchResults.length > 0) {
				setResidents(searchResults);
			} else {
				setResidents([]);
			}
		}
	}

	async function onFilterLineChart(value) {
		setLineChartFilter(value);
		const type = value === 'Month' ? 'MONTH' : 'WEEK';
		const uploadData = await fetchCarePlanUpdateOverTIme(
			homeId,
			token,
			type,
		);

		setPlanUpdatesChart(prevState => ({
			...prevState,
			labels: value === 'Month' ? MONTHS : DAYS,
			datasets: [
				{
					...prevState.datasets[0],
					data: Object.values(uploadData.cn),
				},
				{
					...prevState.datasets[1],
					data: Object.values(uploadData.icp),
				},
				{
					...prevState.datasets[2],
					data: Object.values(uploadData.ocp),
				},
			],
		}));
	}

	function onLastUpdateTab(value) {
		setUpdateTabFilter(value);
		if (value !== 'All') {
			const filterData = allDataRE.filter(item => item.type === value);
			setAllData(filterData);
		} else {
			setAllData(allDataRE);
		}
	}

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = event => {
		setRowsPerPage(+event.target.value);
		setPage(0);
	};

	function formatDateLocal(date) {
		const d = new Date(date);
		return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
	}

	function getSortButtonText(date, isAllTime = false) {
		if (isAllTime) {
			return setFilterButtonText('All Time');
		}

		const todayObj = new Date();
		const yesterdayObj = new Date();
		yesterdayObj.setDate(todayObj.getDate() - 1);

		const today = formatDateLocal(todayObj);
		const yesterday = formatDateLocal(yesterdayObj);

		const startDate = formatDateLocal(date[0].startDate);
		const endDate = formatDateLocal(date[0].endDate);

		if (startDate === today && endDate === today) {
			setFilterButtonText(
				todayObj.toLocaleString('default', { month: 'long' }),
			);
		} else if (startDate === yesterday && endDate === yesterday) {
			setFilterButtonText('Yesterday');
		} else {
			setFilterButtonText(`${startDate} - ${endDate}`);
		}
	}

	function onResetView(type = null, rowSize = 5) {
		setViewType(type);
		setRowsPerPage(rowSize);
	}

	function onFilterDocInResidentView(value) {
		setSelectedDocType(value);
		if (value !== 'All') {
			const filterData = residentReFiles.filter(
				item => item.type === value,
			);
			setResidentFiles(filterData);
		} else {
			setResidentFiles(residentReFiles);
		}
	}

	function onChangeToDocumentView() {
		setPreviousView(viewType);
		setViewType(VIEW_TYPES.document);
	}

	async function onViewOrEditResident(
		id,
		notEditable = true,
		isExternalElder = false,
		elderId,
	) {
		if (isExternalElder) {
			const reData = await fetchExternalElder(id, token);
			const noteRes = await fetchElderCareNotes(
				`external/note/elder/${elderId}`,
				token,
			);
			const notes = noteRes.map(noteObj => noteObj.careNote).flat(1);
			const filteredNotes = notes.map(
				item => `${dateConverter(item.createdDate)} - ${item.note}`,
			);

			setResident(reData);
			setResidentCareNotes(filteredNotes);
		} else {
			const reData = await fetchElder(id, token);
			const reFiles = await fetchElderFiles(id, token);
			const notes = await fetchElderCareNotes(`note/elder/${id}`, token);
			const filteredNotes = notes.map(
				note =>
					`${dateConverter(note.createdDate)} - ${note.enhancedNote}`,
			);

			setResident(reData);
			setResidentFiles(reFiles);
			setResidentReFiles(reFiles);
			setResidentCareNotes(filteredNotes);
		}
		setPreviousView(viewType);
		setResidentNotEditable(notEditable);
		setViewType(VIEW_TYPES.resident);
	}

	async function onViewCarePlan(id, type, elderName) {
		setSelectedPlanType(type);
		const planType = type === ITEM_FILTER[3] ? 'optimized' : 'initial';
		const data = await fetchCarePlanById(id, token, planType);

		setSelectedPlanData(data.plan);
		setPreviousView(viewType);
		setSelectedElder(elderName);
		setViewType(VIEW_TYPES.plan);
	}

	function validateInputs() {
		let isValid = true;

		if (!resident.name || resident.name === '') {
			setReErrors(prevState => ({
				...prevState,
				name: {
					status: true,
					msg: 'Enter resident name',
				},
			}));
			isValid = false;
		}

		if (!resident.gender || resident.gender === '') {
			setReErrors(prevState => ({
				...prevState,
				gender: {
					status: true,
					msg: 'Enter resident gender',
				},
			}));
			isValid = false;
		}

		if (!resident.age || resident.age === '') {
			setReErrors(prevState => ({
				...prevState,
				age: {
					status: true,
					msg: 'Enter resident age',
				},
			}));
			isValid = false;
		}

		if (!['male', 'female'].includes(resident.gender.toLowerCase())) {
			setReErrors(prevState => ({
				...prevState,
				gender: {
					status: true,
					msg: 'Gender should be male or female',
				},
			}));
			isValid = false;
		}

		return isValid;
	}

	function onChangeResidentData(value, name) {
		setReErrors(prevState => ({
			...prevState,
			[name]: ERROR_STATE,
		}));

		setResident(prevState => ({
			...prevState,
			[name]: value,
		}));
	}

	async function onSubmitResident() {
		const isInputsValid = validateInputs();
		if (!isInputsValid) {
			return;
		}

		setIsResidentSubmitting(true);

		const body = {
			name: resident.name,
			gender: resident.gender,
			age: parseInt(resident.age),
		};

		const response = await updateElderDetails(resident.id, body, token);

		if (!response.error) {
			setResident(response.content);
			toast.success('Resident data updated.');
		} else {
			toast.error(response.message);
		}

		setIsResidentSubmitting(false);
	}

	async function removeResident(id) {
		const response = await deleteResident(id, token);
		if (response.message === 'Success') {
			onFetchAndStructureAllData();
			toast.success('Resident removed Successfully');
		} else {
			toast.success(response.message);
		}
	}

	async function onDownloadResidentFile(key, type, isForDownload = false) {
		let routePath = '';

		if (type === FILE_ITEM_FILTER[2]) {
			routePath = `plan/initial/file?key=${key}&download=${isForDownload}`;
		} else if (type === FILE_ITEM_FILTER[3]) {
			routePath = `plan/optimized/file?key=${key}&download=${isForDownload}`;
		} else if (type === FILE_ITEM_FILTER[1]) {
			routePath = `elder/file?key=${key}&download=${isForDownload}`;
		} else {
			return;
		}

		const response = await fetchElderDataFile(routePath, token);
		if (!response.error) {
			if (isForDownload) {
				const link = document.createElement('a');
				link.href = response;
				link.setAttribute('download', '');
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			} else {
				window.open(response, '_blank');
			}
		} else {
			toast.error(response.message);
		}
	}

	const handleAccordionExpansion = id => (_, isExpanded) => {
		setExpandedAccordion(isExpanded ? id : false);
	};

	if (viewType === VIEW_TYPES.table) {
		return (
			<ResidentTable
				data={residents}
				onSearch={onSearch}
				searchedText={searchedText}
				page={page}
				rowsPerPage={rowsPerPage}
				isViewExpanded={true}
				handleChangePage={handleChangePage}
				handleChangeRowsPerPage={handleChangeRowsPerPage}
				onClickMore={onResetView}
				onActionView={(id, externalElder, elderId) =>
					onViewOrEditResident(id, true, externalElder, elderId)
				}
				onActionEdit={id => onViewOrEditResident(id, false)}
				onActionDelete={removeResident}
			/>
		);
	} else if (viewType === VIEW_TYPES.plan) {
		return (
			<PlanOverview
				planData={selectedPlanData}
				selectedElder={selectedElder}
				planType={selectedPlanType}
				expanded={expandedAccordion}
				handleExpansion={handleAccordionExpansion}
				goBack={onResetView}
			/>
		);
	} else if (viewType === VIEW_TYPES.resident) {
		return (
			<ResidentOverview
				resident={resident}
				residentFiles={residentFiles}
				errors={reErrors}
				selectedItem={selectedDocType}
				onFilterDataTile={onFilterDocInResidentView}
				onClickMore={onChangeToDocumentView}
				goBack={() =>
					onResetView(
						previousView,
						previousView === VIEW_TYPES.table ? 10 : 5,
					)
				}
				isSaveBtnDisabled={isResidentNotEditable}
				onChangeData={onChangeResidentData}
				onClickSave={onSubmitResident}
				isLoading={isResidentSubmitting}
				onDownloadResidentFile={onDownloadResidentFile}
				careNotes={residentCareNotes}
			/>
		);
	} else if (viewType === VIEW_TYPES.document) {
		return (
			<DocumentOverview
				residentFiles={residentFiles}
				selectedItem={selectedDocType}
				onFilterDataTile={onFilterDocInResidentView}
				goBack={() => onResetView(VIEW_TYPES.resident)}
				onDownloadResidentFile={onDownloadResidentFile}
			/>
		);
	} else {
		return (
			<Grid
				container
				sx={{
					marginLeft: '30px',
					marginTop: '16px',
					backgroundColor: '#FFFFFF',
				}}>
				<Grid
					container
					sx={{
						backgroundColor: '#FFFFFF',
						borderRadius: '5px',
						marginLeft: '30px',
						paddingTop: '16px',
						paddingX: '8px',
					}}>
					<Grid
						container
						sx={{
							padding: '10px',
							flexDirection: 'row',
							display: 'flex',
							justifyContent: 'space-between',
						}}>
						<Typography
							sx={{
								fontSize: '18px',
								fontWeight: '600',
								color: '#000000',
							}}>
							System Data & Analytics
						</Typography>
						<Grid
							sx={{
								position: 'relative',
								display: 'inline-block',
							}}
							ref={pickerRef}>
							<IconButton
								disableRipple
								sx={{
									alignItems: 'center',
									backgroundColor: '#F5F5F5',
									padding: '8px 16px 8px 16px',
									borderRadius: '5px',
								}}
								onClick={() => setShowPicker(!showPicker)}>
								<CalenderIcon />
								<Typography
									sx={{
										fontSize: '12px',
										fontWeight: '700',
										marginLeft: '5px',
										color: MAIN_THEME_COLOR,
									}}>
									{filterButtonText}
								</Typography>
							</IconButton>
							{showPicker && (
								<Grid
									sx={{
										position: 'absolute',
										top: '100%',
										right: 0,
										zIndex: 999,
										marginTop: '8px',
										boxShadow:
											'0 4px 8px rgba(0, 0, 0, 0.1)',
										width: 'auto',
										backgroundColor: '#FFFFFF',
									}}>
									<Grid
										sx={{
											position: 'relative',
											paddingBottom: '30px',
										}}>
										<DateRange
											locale={enUS}
											editableDateInputs={true}
											onChange={item =>
												setRange([item.selection])
											}
											moveRangeOnFirstSelection={false}
											ranges={range}
											maxDate={new Date()}
											rangeColors={[
												MAIN_THEME_COLOR,
												MAIN_THEME_COLOR,
												MAIN_THEME_COLOR,
											]}
										/>
										<Grid
											sx={{
												position: 'absolute',
												bottom: '5px',
												right: '10px',
												zIndex: 10,
											}}>
											<Button
												onClick={() =>
													getCarePlanStats(true)
												}
												style={{
													padding: '3px 16px',
													color: MAIN_THEME_COLOR,
													border: `1px solid ${MAIN_THEME_COLOR}`,
													borderRadius: '4px',
													textTransform: 'none',
													marginRight: '5px',
												}}>
												All Time
											</Button>
											<Button
												onClick={() =>
													getCarePlanStats()
												}
												style={{
													padding: '4px 16px',
													backgroundColor:
														MAIN_THEME_COLOR,
													color: 'white',
													border: 'none',
													borderRadius: '4px',
													textTransform: 'none',
												}}>
												Apply
											</Button>
										</Grid>
									</Grid>
								</Grid>
							)}
						</Grid>
					</Grid>
					<Grid
						container
						columnGap={1}
						rowGap={1}
						sx={{
							flexDirection: 'row',
							padding: '10px',
							flexWrap: {
								xs: 'wrap',
								sm: 'nowrap',
							},
						}}>
						<MetricTile
							count={metrics.cn}
							name="Care Notes"
							TileIcon={CareNoteIcon}
							description={'Clinical Documentation'}
						/>
						<MetricTile
							count={metrics.icp}
							name="Initial Care Plans"
							TileIcon={ICPIcon}
							description={'Treatment Planning'}
						/>
						<MetricTile
							count={metrics.ocp}
							name="Care Plans Optimized"
							TileIcon={OCPIcon}
							description={'Plan Refinement'}
						/>
						<MetricTile
							count={`${Math.round(metrics.planPer)}%`}
							name="Average Optimizations Frequency"
							TileIcon={PlanPerIcon}
							description={'Per Resident'}
						/>
					</Grid>
					<Grid container>
						<Grid
							item
							xs={12}
							md={7}
							padding={'20px'}
							flexDirection={'column'}>
							<Grid container paddingY={'16px'}>
								<Typography
									color={'#5E5C5C'}
									fontSize={'18px'}
									fontWeight={'600'}>
									Resident activity comparison
								</Typography>
							</Grid>
							<Grid
								container
								justifyContent={'space-between'}
								alignItems={'center'}>
								<Typography color={'#5E5C5C'} fontSize={'16px'}>
									Numbers of updates
								</Typography>
								<Grid
									item
									width={'50%'}
									flexDirection={'row'}
									display={'flex'}>
									{Object.keys(ITEMS_NAME).map(
										(key, index) => {
											return (
												<ItemTile
													key={index}
													color={GRAPH_COLORS[key]}
													name={ITEMS_NAME[key]}
												/>
											);
										},
									)}
								</Grid>
							</Grid>
							<Grid container paddingTop={'20px'}>
								{residentActivityChart.labels.length > 0 ? (
									<div
										style={{
											width: `${Math.max(720, residentActivityChart.labels.length * 15)}px`,
											overflowX: 'auto',
										}}>
										<div
											style={{
												minWidth: `${residentActivityChart.labels.length * 40}px`,
												height: '400px',
											}}>
											<Bar
												data={residentActivityChart}
												options={barCharOptions}
											/>
										</div>
									</div>
								) : (
									<Grid
										container
										justifyContent={'center'}
										alignItems={'center'}
										height={'400px'}>
										<Typography>
											No data to display graph
										</Typography>
									</Grid>
								)}
							</Grid>
						</Grid>
						<Grid item xs={12} md={5} paddingTop={'24px'}>
							<Grid
								container
								sx={{
									backgroundColor: '#FCFDFF',
									borderRadius: '6px',
									flexDirection: 'row',
									alignItems: 'center',
									display: 'flex',
									paddingX: '24px',
									paddingY: '12px',
									border: '0.6px solid #F1F3FA',
								}}>
								<Grid container paddingTop={'16px'}>
									<Typography
										color={'#5E5C5C'}
										fontSize={'18px'}
										fontWeight={'600'}>
										Distribution of Residents by Care Status
									</Typography>
								</Grid>
								<Grid
									sx={{
										height: '320px',
										width: '100%',
										padding: '20px',
										justifyContent: 'center',
										alignItems: 'center',
										position: 'relative',
									}}>
									<Grid
										sx={{
											position: 'absolute',
											top: '50%',
											left: '50%',
											transform: 'translate(-50%, -50%)',
											color: '#000000',
											textAlign: 'center',
										}}>
										<Typography
											color="#5E5C5C"
											fontSize={'24px'}
											fontWeight={'600'}>
											{`${careStatFrequency.total}%`}
										</Typography>
										<Typography
											color="#5E5C5C"
											fontSize={'16px'}
											fontWeight={'400'}>
											Total Care Status
										</Typography>
									</Grid>
									<Doughnut
										data={careStatusChart}
										options={doughnutChartOptions}
									/>
								</Grid>
								<Grid
									container
									width={'100%'}
									padding={'5px'}
									justifyContent={'center'}>
									<Grid
										container
										padding={'10px'}
										justifyContent={'center'}>
										<UserAnalyticsSection
											name="Care Note"
											percentage={`${careStatFrequency.cn}%`}
											color={GRAPH_COLORS.cn}
										/>
										<UserAnalyticsSection
											name="Initial Plans"
											percentage={`${careStatFrequency.icp}%`}
											color={GRAPH_COLORS.icp}
										/>
									</Grid>
									<UserAnalyticsSection
										name="Optimized Plans"
										percentage={`${careStatFrequency.ocp}%`}
										color={GRAPH_COLORS.ocp}
									/>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
				<Grid
					container
					width={'100%'}
					marginLeft={'30px'}
					marginTop={'10px'}>
					<Grid
						item
						xs={12}
						md={6.9}
						sx={{
							backgroundColor: '#FFFFFF',
							paddingX: '18px',
							borderRadius: '5px',
						}}>
						<Grid
							container
							paddingY={'16px'}
							alignItems={'center'}
							justifyContent={'space-between'}>
							<Typography
								color={'#191919'}
								fontSize={'18px'}
								fontWeight={'600'}>
								Care Plan Updates Over Time
							</Typography>
							<Grid item flexDirection={'row'} display={'flex'}>
								{DATE_FILTER.map((item, index) => {
									return (
										<FilterTile
											key={index}
											name={item}
											isSelected={
												item === lineChartFilter
											}
											onClick={() =>
												onFilterLineChart(item)
											}
										/>
									);
								})}
							</Grid>
						</Grid>
						<Grid container justifyContent={'space-between'}>
							<Typography color={'#5E5C5C'} fontSize={'16px'}>
								Numbers of updates
							</Typography>
							<Grid
								item
								width={'50%'}
								flexDirection={'row'}
								display={'flex'}>
								{Object.keys(ITEMS_NAME).map((key, index) => {
									return (
										<ItemTile
											key={index}
											color={GRAPH_COLORS[key]}
											name={ITEMS_NAME[key]}
										/>
									);
								})}
							</Grid>
						</Grid>
						<Grid container paddingTop={'20px'} height={400}>
							<Line
								data={planUpdatesChart}
								options={lineCharOptions}
							/>
						</Grid>
					</Grid>
					<Grid
						item
						xs={12}
						md={5}
						sx={{
							backgroundColor: '#FFFFFF',
							borderRadius: '5px',
							flexDirection: 'column',
							marginLeft: '10px',
							paddingX: '16px',
							paddingY: '8px',
						}}>
						<Grid container paddingTop={'16px'}>
							<Typography
								color={'#5E5C5C'}
								fontSize={'18px'}
								fontWeight={'600'}>
								Latest Updates
							</Typography>
						</Grid>
						<Grid
							container
							paddingY="10px"
							justifyContent={'center'}>
							{ITEM_FILTER.map((item, index) => {
								return (
									<FilterTile
										key={index}
										name={item}
										isSelected={item === updateTabFilter}
										onClick={() => onLastUpdateTab(item)}
									/>
								);
							})}
						</Grid>
						<Box
							ref={scrollRef}
							sx={{
								maxHeight: '380px',
								overflowY: 'auto',
								paddingBottom: '5px',
								paddingX: '10px',
							}}>
							{allData.length > 0 ? (
								<Grid container rowGap={1}>
									{allData.map((item, index) => {
										return (
											<DataTile
												key={index}
												elderId={item.elderId}
												elderName={item.elderName}
												admin={item.admin}
												type={item.type}
												content={item.content}
												addedDate={item.addedDate}
												filters={ITEM_FILTER}
												onViewPlan={() =>
													onViewCarePlan(
														item.id,
														item.type,
														`${item.elderId}_${item.elderName}`,
													)
												}
											/>
										);
									})}
								</Grid>
							) : (
								<Grid
									container
									justifyContent={'center'}
									alignItems={'center'}
									marginTop={'50px'}>
									<Typography
										sx={{
											whiteSpace: 'normal',
											wordBreak: 'break-word',
											padding: '10px',
											fontSize: '16px',
											color: '#3D4A58',
										}}
										fontWeight={'500'}>
										No Data
									</Typography>
								</Grid>
							)}
						</Box>
					</Grid>
				</Grid>
				<ResidentTable
					data={residents}
					onSearch={onSearch}
					searchedText={searchedText}
					page={page}
					rowsPerPage={rowsPerPage}
					isViewExpanded={false}
					handleChangePage={handleChangePage}
					handleChangeRowsPerPage={handleChangeRowsPerPage}
					onClickMore={() => onResetView(VIEW_TYPES.table, 10)}
					onActionView={(id, externalElder, elderId) =>
						onViewOrEditResident(id, true, externalElder, elderId)
					}
					onActionEdit={id => onViewOrEditResident(id, false)}
					onActionDelete={removeResident}
					isLoading={isLoading}
				/>
			</Grid>
		);
	}
}

export default SystemUsageDashboard;
